import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core'
import { Typography, Link, InvoiceStatus, Avatar, Tooltip, ProviderPopover } from 'components'
import TableCell from '@material-ui/core/TableCell'
import { colors, CONSTANTS, routes } from 'constants/index'
import * as moment from 'moment-timezone'
import { useDispatch, useSelector } from 'react-redux'
import {
  navigateToProConnectionsInNewTab,
  navigateToCaseDetailsInNewTab,
  navigateToEditCaseInNewTab,
} from 'store/actions/navigation'
import { Link as RouterLink } from 'react-router-dom'
import { ReactComponent as ShareIcon } from 'static/cases/share.svg'
import PeopleWithAccessModal from '../cases/PeopleWithAccessModal'

const getInitials = (firstName, lastName) => {
  const first = firstName ? firstName.charAt(0).toUpperCase() : ''
  const last = lastName ? lastName.charAt(0).toUpperCase() : ''
  return first + last || '?'
}

const useStyles = makeStyles({
  link: {
    color: colors.BLUE_20,
    textDecoration: 'none',
    fontSize: 14,
  },
  tableCellTextClass: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  providersContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 0,
    paddingRight: 8,
    justifyContent: 'center',
  },
  providerAvatar: {
    width: 24,
    height: 24,
    fontSize: 9.75,
    fontWeight: 400,
    marginRight: -8,
    cursor: 'pointer',
    border: `2px solid ${colors.WHITE}`,
    '&:last-child': {
      marginRight: 0,
    },
  },
  moreProvidersAvatar: {
    width: 24,
    height: 24,
    fontSize: 9.75,
    fontWeight: 400,
    marginRight: -8,
    cursor: 'pointer',
    border: `2px solid ${colors.WHITE}`,
    backgroundColor: colors.BASIC.COLOR[80],
    color: colors.WHITE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: '13.5px',
    textTransform: 'uppercase',
  },
  caseNameContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  shareButton: {
    width: 16,
    height: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  shareButtonRotated: {
    transform: 'rotate(180deg)',
  },
  shareTooltip: {
    backgroundColor: colors.BASIC.COLOR[80],
    color: colors.WHITE,
    fontSize: 13,
    fontWeight: 'normal',
  },
})

const ProInvoicesTableRow = ({ data, timezone, isAccountant }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const currentUser = useSelector(state => state.auth.currentUser)
  const [providerPopoverAnchor, setProviderPopoverAnchor] = useState(null)
  const [selectedProvider, setSelectedProvider] = useState(null)
  const [showPeopleModal, setShowPeopleModal] = useState(false)

  const getCurrencySymbol = () => {
    return data?.currency === 'CAD' ? 'C$' : '$'
  }
  const invoiceDate = moment(data.invoiceDate)
    .tz(timezone)
    .format(CONSTANTS.DATE_FORMAT.FORMAT_ONE)

  const navigateToClient = () => {
    dispatch(navigateToProConnectionsInNewTab({ clientName: data.clientFullName }))
  }

  const navigateToCase = () => {
    dispatch(navigateToCaseDetailsInNewTab(data.case))
  }

  const handleProviderAvatarClick = (provider, e) => {
    e.stopPropagation()
    setSelectedProvider(provider)
    setProviderPopoverAnchor(e.currentTarget)
  }

  const handleProviderPopoverClose = () => {
    setProviderPopoverAnchor(null)
    setSelectedProvider(null)
  }

  const handleShareIconClick = e => {
    e.stopPropagation()
    setShowPeopleModal(true)
  }

  const handleMoreProvidersClick = e => {
    e.stopPropagation()
    setShowPeopleModal(true)
  }

  // Determine if invoice is shared by me or shared with me
  const contributors = data.contributors || []
  const isSharedByMe = data.is_shared && data?.shared_with_count > 0
  const isSharedWithMe = data.is_shared && !!data?.shared_by && data?.shared_with_count === 0

  const hasMultipleProviders = contributors.length >= 3
  const visibleProviders = hasMultipleProviders ? contributors.slice(0, 2) : contributors
  const remainingCount = hasMultipleProviders ? contributors.length - 2 : 0

  const getShareTooltip = () => {
    if (isSharedByMe) {
      return `Project shared by you with ${data.shared_with_count || 0} provider(s)`
    } else if (isSharedWithMe && data?.shared_by) {
      return `Shared with you by ${data?.shared_by}`
    }
    return ''
  }

  const clientsName = data.clientInfo.isDeleted ? (
    <Typography variant="body2" classes={{ root: classes.tableCellTextClass }}>
      {data.clientFullName}
    </Typography>
  ) : (
    <Link onClick={navigateToClient} className={classes.link}>
      {data.clientFullName}
    </Link>
  )

  return (
    <>
      <TableCell>
        <Typography variant="body2" classes={{ root: classes.tableCellTextClass }}>
          <Link
            className={classes.link}
            component={RouterLink}
            to={`${routes.getProAccountFinancesInvoicesViewPath()}/${data.uuid}`}
          >
            {data.invoiceNumber}
          </Link>
        </Typography>
      </TableCell>
      {isAccountant && (
        <TableCell>
          {contributors.length > 0 && (
            <>
              <div className={classes.providersContainer}>
                {visibleProviders.map((provider, index) => (
                  <Avatar
                    key={provider.username || index}
                    className={classes.providerAvatar}
                    src={provider.photo_thumbnail || provider.photo || null}
                    onClick={e => handleProviderAvatarClick(provider, e)}
                  >
                    {!provider.photo_thumbnail &&
                      !provider.photo &&
                      getInitials(provider.first_name, provider.last_name)}
                  </Avatar>
                ))}
                {hasMultipleProviders && (
                  <Avatar className={classes.moreProvidersAvatar} onClick={handleMoreProvidersClick}>
                    +{remainingCount}
                  </Avatar>
                )}
              </div>
              {selectedProvider && (
                <ProviderPopover
                  provider={selectedProvider}
                  anchorEl={providerPopoverAnchor}
                  open={Boolean(providerPopoverAnchor)}
                  onClose={handleProviderPopoverClose}
                />
              )}
            </>
          )}
        </TableCell>
      )}
      <TableCell>
        <Typography variant="body2" classes={{ root: classes.tableCellTextClass }}>
          {invoiceDate}
        </Typography>
      </TableCell>
      <TableCell>{clientsName}</TableCell>
      <TableCell>
        <div className={classes.caseNameContainer}>
          <Link onClick={navigateToCase} className={classes.link}>
            {data.caseName}
          </Link>
          {data.is_shared && data?.isProjectShared && getShareTooltip() && (
            <Tooltip tooltipClass={classes.shareTooltip} title={getShareTooltip()} placement="bottom-start">
              <ShareIcon
                className={`${classes.shareButton} ${isSharedWithMe ? classes.shareButtonRotated : ''}`}
                onClick={handleShareIconClick}
              />
            </Tooltip>
          )}
        </div>
      </TableCell>
      <TableCell align={'right'}>
        <Typography variant="body2" classes={{ root: classes.tableCellTextClass }}>
          {getCurrencySymbol()} {data.amountDue}
        </Typography>
      </TableCell>
      <TableCell align={'right'}>
        <Typography variant="body2" classes={{ root: classes.tableCellTextClass }}>
          {getCurrencySymbol()} {data.amountPaid}
        </Typography>
      </TableCell>
      <TableCell>
        <InvoiceStatus status={data.status} />
      </TableCell>
      <PeopleWithAccessModal isOpen={showPeopleModal} onClose={() => setShowPeopleModal(false)} caseUuid={data.case} />
    </>
  )
}

export default ProInvoicesTableRow
